// Copyright ©️ 2026 eVolve MEP, LLC

import './SingleSelectExtension';
import './MultiSelectExtension';
import './WindowSelectExtension';

import { addViewerListener } from 'components/Autodesk/extensions/utils';
import { isNil, isNotNil } from 'helpers/isNotNil';

import {
  MULTI_SELECT_TOOL_NAME,
  SELECTION_TOOLS,
  type SelectionMouseEvent,
  type SelectionTool,
  SINGLE_SELECT_TOOL_NAME,
  WINDOW_SELECT_TOOL_NAME,
} from './types';

const { Autodesk } = window;

const SELECTION_MODE_ICON_CLASS = {
  [SINGLE_SELECT_TOOL_NAME]: 'fa-mouse-pointer',
  [MULTI_SELECT_TOOL_NAME]: 'fa-object-ungroup',
  [WINDOW_SELECT_TOOL_NAME]: 'fa-window-maximize',
} as const satisfies Record<SelectionTool, string>;

const SELECTION_MODE_TOOLTIP = {
  [SINGLE_SELECT_TOOL_NAME]: 'Single Select',
  [MULTI_SELECT_TOOL_NAME]: 'Multi Select (Shift)',
  [WINDOW_SELECT_TOOL_NAME]: 'Window Select (Ctrl)',
} as const satisfies Record<SelectionTool, string>;

const FAS = 'fas';

const DISABLE_NAVIGATION_TOOL_NAME = 'disable-navigation-tool';

const isLeftClick = (button: number) => button === 0;

/**
 * This tool is used to disable the default navigation behavior in the Autodesk Viewer while one of the selection tools is active.
 */
class DisableNavigationTool implements Autodesk.Viewing.ToolInterface {
  active: boolean;
  isDragging: boolean;
  viewer: Autodesk.Viewing.GuiViewer3D;

  constructor(viewer: Autodesk.Viewing.GuiViewer3D) {
    this.viewer = viewer;

    this.active = false;
    this.isDragging = false;
  }

  /* oxlint-disable no-empty-function */
  register() {}

  /* oxlint-disable no-empty-function */
  deregister() {}

  activate() {
    this.active = true;
  }

  deactivate() {
    this.active = false;
  }

  getName() {
    return DISABLE_NAVIGATION_TOOL_NAME;
  }

  getNames() {
    return [DISABLE_NAVIGATION_TOOL_NAME];
  }

  update(_timestamp: number) {
    return false;
  }

  // Tools get called in the order of their priority (higher numbers first). Default tools have a priority of 0.
  getPriority() {
    return 96;
  }

  // Prevent double clicks while the tool is active.
  // The default behavior sets the selection to the double-clicked element only.
  handleDoubleClick(event: SelectionMouseEvent, button: number) {
    if (!this.active) return false;

    return isLeftClick(button);
  }

  // NOTE: The following methods are included to disable the default navigation behavior:

  handleGesture(event: SelectionMouseEvent) {
    switch (event.type) {
      case 'dragstart':
        return this.handleButtonDown(event, 0);

      case 'dragmove':
        return this.handleMouseMove(event);

      case 'dragend':
        return this.handleButtonUp(event, 0);

      default:
        break;
    }

    return false;
  }

  handleButtonDown(_event: SelectionMouseEvent, button: number) {
    if (!this.active) return false;

    if (isLeftClick(button)) return true;

    this.isDragging = true;

    return true;
  }

  handleMouseMove(_event: SelectionMouseEvent) {
    if (!this.active) return false;

    return this.isDragging;
  }

  handleButtonUp(_event: SelectionMouseEvent, button: number) {
    if (!this.active) return false;

    if (!isLeftClick(button)) return false;

    this.isDragging = false;

    return true;
  }
}

export class SelectionToolsExtension extends Autodesk.Viewing.Extension {
  disableNavigationTool: DisableNavigationTool;
  selectedOption: SelectionTool | null;
  cleanUpFns: (() => void)[] = [];

  constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: Autodesk.Viewing.ExtensionOptions) {
    super(viewer, options);
    this.selectedOption = null;
    this.disableNavigationTool = new DisableNavigationTool(viewer);
  }

  async load() {
    const navigationChanged = this.handleNavigationModeChanged.bind(this);
    this.cleanUpFns.push(() =>
      addViewerListener(this.viewer, Autodesk.Viewing.NAVIGATION_MODE_CHANGED_EVENT, navigationChanged),
    );

    if (isNotNil(this.viewer.toolbar)) {
      // Toolbar is already available, create the UI.
      await this.createUI();
    } else {
      // Toolbar hasn't been created yet, wait until we get notification of its creation.
      this.viewer.addEventListener(Autodesk.Viewing.TOOLBAR_CREATED_EVENT, this.onToolbarCreated.bind(this), {
        once: true,
      });
    }

    return true;
  }

  unload() {
    this.viewer.toolController.deactivateTool(DISABLE_NAVIGATION_TOOL_NAME);
    this.viewer.toolController.deregisterTool(this.disableNavigationTool);

    this.viewer.unloadExtension('SingleSelectExtension');
    this.viewer.unloadExtension('MultiSelectExtension');
    this.viewer.unloadExtension('WindowSelectExtension');

    this.viewer.toolbar.removeControl('selectTools');
    while (this.cleanUpFns.length > 0) {
      const cleanUpFn = this.cleanUpFns.pop();
      cleanUpFn?.();
    }
    return true;
  }

  isSelectionTool(navigationTool: string): navigationTool is SelectionTool {
    return (SELECTION_TOOLS as readonly string[]).includes(navigationTool);
  }

  handleNavigationModeChanged() {
    const navigationTool = this.viewer.getActiveNavigationTool();
    const isSelectionTool = this.isSelectionTool(navigationTool);

    if (isNil(this.viewer.toolbar)) return;

    const navToolsControlGroup = this.viewer.toolbar.getControl('navTools') as Autodesk.Viewing.UI.ControlGroup;
    const selectToolsDropdown = navToolsControlGroup.getControl('selectTools') as Autodesk.Viewing.UI.ComboButton;

    if (isNil(selectToolsDropdown)) return;

    if (isSelectionTool) {
      this.viewer.toolController.activateTool(DISABLE_NAVIGATION_TOOL_NAME);
      selectToolsDropdown.setState(Autodesk.Viewing.UI.Button.State.ACTIVE);
      this.selectedOption = navigationTool;
    } else {
      this.viewer.toolController.deactivateTool(DISABLE_NAVIGATION_TOOL_NAME);
      selectToolsDropdown.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);
      this.selectedOption = null;
    }

    this.updateDropdownMenuIcon();
  }

  async onToolbarCreated() {
    await this.createUI();
  }

  async createUI() {
    // NOTE: We're waiting to register these extensions until the toolbar and viewer.toolController are available.
    await this.viewer.loadExtension('SingleSelectExtension');
    await this.viewer.loadExtension('MultiSelectExtension');
    await this.viewer.loadExtension('WindowSelectExtension');
    this.viewer.toolController.registerTool(this.disableNavigationTool);

    const navToolsControlGroup = this.viewer.toolbar.getControl('navTools') as Autodesk.Viewing.UI.ControlGroup;

    if (isNil(navToolsControlGroup)) return;

    const selectToolsDropdown = new Autodesk.Viewing.UI.ComboButton('selectTools');

    selectToolsDropdown.setToolTip(SELECTION_MODE_TOOLTIP[SINGLE_SELECT_TOOL_NAME]);
    selectToolsDropdown.icon.classList.add(FAS, SELECTION_MODE_ICON_CLASS[SINGLE_SELECT_TOOL_NAME]);

    // Add the buttons to the drop-down menu
    selectToolsDropdown.addControl(this.createSingleSelectButton());
    selectToolsDropdown.addControl(this.createMultiSelectButton());
    selectToolsDropdown.addControl(this.createWindowSelectButton());
    selectToolsDropdown.addControl(this.createClearSelectionButton());

    navToolsControlGroup.addControl(selectToolsDropdown, { index: 0 });
  }

  createClearSelectionButton() {
    const button = new Autodesk.Viewing.UI.Button('toolbar-clear-selection');

    button.onClick = () => this.viewer.clearSelection();
    button.icon.classList.add(FAS, 'fa-times-circle');
    button.setToolTip('Clear Selection (Esc)');

    return button;
  }

  createSingleSelectButton() {
    const button = new Autodesk.Viewing.UI.Button('toolbar-single-select');

    button.onClick = () => this.handleSingleSelectButtonClick();
    button.icon.classList.add(FAS, SELECTION_MODE_ICON_CLASS[SINGLE_SELECT_TOOL_NAME]);
    button.setToolTip(SELECTION_MODE_TOOLTIP[SINGLE_SELECT_TOOL_NAME]);

    return button;
  }

  createMultiSelectButton() {
    const button = new Autodesk.Viewing.UI.Button('toolbar-multi-select');

    button.onClick = () => this.handleMultiSelectButtonClick();
    button.icon.classList.add(FAS, SELECTION_MODE_ICON_CLASS[MULTI_SELECT_TOOL_NAME]);
    button.setToolTip(SELECTION_MODE_TOOLTIP[MULTI_SELECT_TOOL_NAME]);

    return button;
  }

  createWindowSelectButton() {
    const button = new Autodesk.Viewing.UI.Button('toolbar-window-select');

    button.onClick = () => this.handleWindowButtonClick();
    button.icon.classList.add(FAS, SELECTION_MODE_ICON_CLASS[WINDOW_SELECT_TOOL_NAME]);
    button.setToolTip(SELECTION_MODE_TOOLTIP[WINDOW_SELECT_TOOL_NAME]);

    return button;
  }

  changeNavigationTool(navigationTool: SelectionTool) {
    this.selectedOption = navigationTool;
    this.viewer.setActiveNavigationTool(navigationTool);
    this.updateDropdownMenuIcon();
  }

  handleSingleSelectButtonClick() {
    this.changeNavigationTool(SINGLE_SELECT_TOOL_NAME);
  }

  handleMultiSelectButtonClick() {
    this.changeNavigationTool(MULTI_SELECT_TOOL_NAME);
  }

  handleWindowButtonClick() {
    this.changeNavigationTool(WINDOW_SELECT_TOOL_NAME);
  }

  updateDropdownMenuIcon() {
    const navToolsControlGroup = this.viewer.toolbar.getControl('navTools') as Autodesk.Viewing.UI.ControlGroup;
    const selectToolsDropdown = navToolsControlGroup.getControl('selectTools') as Autodesk.Viewing.UI.ComboButton;

    if (isNil(selectToolsDropdown)) return;

    const defaultSelectionTool: SelectionTool = SINGLE_SELECT_TOOL_NAME;

    const iconClass = SELECTION_MODE_ICON_CLASS[this.selectedOption ?? defaultSelectionTool];
    const tooltip = SELECTION_MODE_TOOLTIP[this.selectedOption ?? defaultSelectionTool];

    const selectionModeIconClasses: string[] = Object.values(SELECTION_MODE_ICON_CLASS);

    // Only remove the existing selection icon class.
    selectToolsDropdown.icon.classList.forEach((className) => {
      if (selectionModeIconClasses.includes(className)) {
        selectToolsDropdown.icon.classList.remove(className);
      }
    });

    // Add the updated icon class.
    selectToolsDropdown.icon.classList.add(iconClass);

    selectToolsDropdown.setToolTip(tooltip);
  }
}
